import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const SacroiliacJointDysfunctionCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
    title: "Sacroiliac Joint Dysfunction in New York City",
    metaDescription: "Expert care for sacroiliac joint dysfunction in New York. Learn about treatments to alleviate lower back pain and restore mobility at NY Center for Spinal Disorders.",
    metaKeywords: "Sacroiliac joint dysfunction, SI joint pain, lower back pain NYC, core strengthening, Dr. Shah, Dr. Tindel",
    heroDescription: "Comprehensive treatment for sacroiliac joint dysfunction with personalized care to reduce pain and improve function.",
    forWho: [
      "Your sacroiliac (SI) joints connect the sacrum (the triangle-shaped bone at the base of your spine) to the iliac bones of your pelvis. SI joint dysfunction happens when these joints move too much or too little, leading to lower back and buttock pain that can sometimes radiate down the leg. This discomfort is often mistaken for sciatica or other spinal issues.",
      "Causes include leg length discrepancies, trauma (like a hard fall on the buttocks), pregnancy (due to hormonal changes and added stress on the pelvis), or degenerative arthritis. Because the SI joints are less commonly discussed than other areas of the spine, many patients don’t realize these joints can be a source of chronic pain."
    ],
    conditionInfo: [
      "If you have stubborn lower back or buttock pain, especially when standing or walking, SI joint dysfunction could be the culprit. Our spine surgeons at the NY Center for Spinal Disorders can accurately diagnose this condition and guide you on the best ways to restore comfort and stability.",
    ],
    treatment: [
      "Conservative care includes bracing, physical therapy, and injections.",
      "Minimally invasive SI joint fusion is an option for severe, unrelenting pain.",
      "Most recoveries involve gradual return to normal activities, with an emphasis on core stability."
    ],
    postCare: [
      "Follow-up visits ensure the SI joints stay aligned and pain remains controlled.",
      "Patient education fosters safe movement techniques."
    ],
    nonSurgicalOptions: [
      "Physical therapy (core strengthening)",
      "Joint injections",
      "Supportive bracing"
    ],
    faqs: [
      {
        id: 1,
        question: "Is SI joint pain the same as sciatica?",
        answer: "They can mimic each other, but proper diagnosis differentiates them."
      },
      {
        id: 2,
        question: "Can this improve without surgery?",
        answer: "Yes. Many find relief through therapy, injections, and lifestyle changes."
      }
    ]
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
      <title>{pageData.title} - NYC for Spinal Disorders</title>
      <meta name="description" content={pageData.metaDescription} />
      <meta name="keywords" content={pageData.metaKeywords} />
      <meta name="author" content="New York Center for Spinal Disorders" />
      <meta property="og:title" content={pageData.title} />
      <meta property="og:description" content={pageData.metaDescription} />
      <meta property="og:image" content="/images/logo-header.png" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      
      <Header />

      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt={pageData.title}
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>





        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SacroiliacJointDysfunctionCondition;